<template>
  <div class="zen-search-mobile" :class="{ 'is-disabled': !isDataLoaded }">
    <!-- Trigger: Search icon button -->
    <button class="search-trigger" :disabled="!isDataLoaded" @click="expandSearch">
      <IconRenderer name="search-outline" size="small" class="trigger-icon" />
    </button>

    <!-- Expanded Search Overlay -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isExpanded" class="search-overlay">
          <!-- Backdrop -->
          <div class="search-backdrop" @click="collapseSearch"></div>

          <!-- Search Container -->
          <div class="search-container">
            <ZenSearchInput
              ref="searchInputRef"
              :force-overlay-mode="true"
              :show-backdrop="false"
              @navigate="handleNavigate"
              @result-selected="collapseSearch"
              @close="collapseSearch"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed } from 'vue';
import ZenSearchInput from './ZenSearchInput.vue';
import type { SearchResult } from '@/common/composables/useQuickSearch';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUserRoutes } from '@/common/composables/useUserRoutes';
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

const emit = defineEmits<{
  navigate: [result: SearchResult];
  close: [];
}>();

// Data loading state (needed for the trigger button)
const { hasModules, loading: modulesLoading } = useModuleApi();
const { hasRoutes } = useUserRoutes();

const isDataLoaded = computed(() => {
  return hasModules.value && hasRoutes.value && !modulesLoading.value;
});

// Mobile-specific state
const isExpanded = ref(false);
const searchInputRef = ref<InstanceType<typeof ZenSearchInput> | null>(null);

// Expand search overlay
function expandSearch() {
  if (!isDataLoaded.value) return;
  isExpanded.value = true;
  // Focus input after animation
  nextTick(() => {
    setTimeout(() => {
      searchInputRef.value?.focusInput();
    }, 100);
  });
}

// Collapse search overlay
function collapseSearch() {
  isExpanded.value = false;
  searchInputRef.value?.closeSearch();
  emit('close');
}

// Handle navigation
function handleNavigate(result: SearchResult) {
  emit('navigate', result);
}
</script>

<style scoped>
.zen-search-mobile {
  display: flex;
  align-items: center;
}

/* Trigger Button */
.search-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.search-trigger:hover {
  background: rgba(var(--ion-color-medium-rgb), 0.1);
}

.search-trigger:active {
  background: rgba(var(--ion-color-medium-rgb), 0.2);
}

.trigger-icon {
  font-size: 22px;
  color: var(--ion-text-color);
}

/* Disabled State */
.zen-search-mobile.is-disabled .search-trigger {
  cursor: not-allowed;
  opacity: 0.4;
}

.zen-search-mobile.is-disabled .search-trigger:hover {
  background: transparent;
}

.search-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

/* Search Overlay */
.search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.search-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.search-container {
  position: relative;
  z-index: 1;
  background: var(--ion-background-color);
  padding: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  box-shadow: var(--ion-box-shadow-hover);
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 16px 16px 0 0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .search-container,
.fade-leave-active .search-container {
  transition: transform 0.2s ease;
}

.fade-enter-from .search-container,
.fade-leave-to .search-container {
  transform: translateY(100%);
}
</style>
